import React, { useState, useContext, useEffect, useMemo } from "react";
import { db } from "services/firebase";
import { AuthContext } from "context/Auth";
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Grid,
  Select,
  MenuItem,
  FormControl,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import TrendingUpRounded from "@mui/icons-material/TrendingUpRounded";
import MonetizationOnRounded from "@mui/icons-material/MonetizationOnRounded";
import SmartToyRounded from "@mui/icons-material/SmartToyRounded";
import CallMadeRounded from "@mui/icons-material/CallMadeRounded";
import { calculateAggregatedCost, formatCost } from "utils/costCalculator";
import DashboardPageHeader from "components/common/DashboardPageHeader";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Line } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Filler,
  Tooltip,
  Legend
);

const PROVIDER_COLORS = {
  openai: { bg: "rgba(16, 163, 127, 0.7)", border: "rgb(16, 163, 127)" },
  anthropic: { bg: "rgba(205, 92, 50, 0.7)", border: "rgb(205, 92, 50)" },
  google: { bg: "rgba(66, 133, 244, 0.7)", border: "rgb(66, 133, 244)" },
  other: { bg: "rgba(130, 130, 130, 0.7)", border: "rgb(130, 130, 130)" },
};

const RANGE_OPTIONS = [
  { value: 7, label: "7 days" },
  { value: 30, label: "30 days" },
  { value: 60, label: "60 days" },
  { value: 90, label: "90 days" },
];

const ORG_PALETTE = [
  { bg: "rgba(99, 102, 241, 0.7)", border: "rgb(99, 102, 241)" },
  { bg: "rgba(236, 72, 153, 0.7)", border: "rgb(236, 72, 153)" },
  { bg: "rgba(245, 158, 11, 0.7)", border: "rgb(245, 158, 11)" },
  { bg: "rgba(16, 185, 129, 0.7)", border: "rgb(16, 185, 129)" },
  { bg: "rgba(59, 130, 246, 0.7)", border: "rgb(59, 130, 246)" },
  { bg: "rgba(239, 68, 68, 0.7)", border: "rgb(239, 68, 68)" },
  { bg: "rgba(139, 92, 246, 0.7)", border: "rgb(139, 92, 246)" },
  { bg: "rgba(20, 184, 166, 0.7)", border: "rgb(20, 184, 166)" },
];

function buildDailyBuckets(calls, daysBack) {
  const buckets = {};
  const now = new Date();

  for (let i = daysBack - 1; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    buckets[key] = { total: 0, openai: 0, anthropic: 0, google: 0, other: 0, calls: 0 };
  }

  calls.forEach(({ date, cost, provider }) => {
    const key = date.toDate().toISOString().slice(0, 10);
    if (!buckets[key]) return;
    const c = cost ?? 0;
    buckets[key].total += c;
    buckets[key].calls += 1;
    const p = provider in PROVIDER_COLORS ? provider : "other";
    buckets[key][p] += c;
  });

  return buckets;
}

function buildOrgAggregates(calls) {
  const orgs = {};
  calls.forEach(({ cost, organization_id, calls: _c }) => {
    const key = organization_id || "(no org)";
    if (!orgs[key]) orgs[key] = { cost: 0, calls: 0 };
    orgs[key].cost += cost ?? 0;
    orgs[key].calls += 1;
  });
  return orgs;
}

const OrgTable = ({ calls, totalCost }) => {
  const orgs = useMemo(() => buildOrgAggregates(calls), [calls]);
  const sorted = Object.entries(orgs).sort(([, a], [, b]) => b.cost - a.cost);

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Organization ID</TableCell>
            <TableCell align="right">API Calls</TableCell>
            <TableCell align="right">Cost</TableCell>
            <TableCell align="right">% of Total</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map(([orgId, data]) => (
            <TableRow key={orgId}>
              <TableCell>
                <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.82rem" }}>
                  {orgId}
                </Typography>
              </TableCell>
              <TableCell align="right">{data.calls.toLocaleString()}</TableCell>
              <TableCell align="right">
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  ${data.cost.toFixed(4)}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2" color="text.secondary">
                  {totalCost > 0 ? `${((data.cost / totalCost) * 100).toFixed(1)}%` : "—"}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const LLMCostChart = ({ calls, loading, rangedays, onRangeChange }) => {
  const [view, setView] = useState("cumulative");

  const { labels, dailyByProvider, cumulativeLine, totalCost, totalCalls } = useMemo(() => {
    if (!calls.length) {
      return { labels: [], dailyByProvider: {}, cumulativeLine: [], totalCost: 0, totalCalls: 0 };
    }

    const buckets = buildDailyBuckets(calls, rangedays);
    const days = Object.keys(buckets).sort();
    const labels = days.map((d) => {
      const dt = new Date(d + "T00:00:00");
      return dt.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    });

    const providers = ["openai", "anthropic", "google", "other"];
    const dailyByProvider = {};
    providers.forEach((p) => {
      dailyByProvider[p] = days.map((d) => buckets[d][p]);
    });

    let running = 0;
    const cumulativeLine = days.map((d) => {
      running += buckets[d].total;
      return Number(running.toFixed(6));
    });

    const totalCost = running;
    const totalCalls = days.reduce((s, d) => s + buckets[d].calls, 0);

    return { labels, dailyByProvider, cumulativeLine, totalCost, totalCalls };
  }, [calls, rangedays]);

  const orgData = useMemo(() => {
    if (!calls.length) return { labels: [], datasets: [] };
    const orgs = buildOrgAggregates(calls);
    const sorted = Object.entries(orgs).sort(([, a], [, b]) => b.cost - a.cost);
    return {
      labels: sorted.map(([id]) => id),
      datasets: [
        {
          label: "Cost by Organization ($)",
          data: sorted.map(([, d]) => Number(d.cost.toFixed(6))),
          backgroundColor: sorted.map((_, i) => ORG_PALETTE[i % ORG_PALETTE.length].bg),
          borderColor: sorted.map((_, i) => ORG_PALETTE[i % ORG_PALETTE.length].border),
          borderWidth: 1,
        },
      ],
    };
  }, [calls]);

  const barData = {
    labels,
    datasets: Object.entries(dailyByProvider)
      .filter(([, vals]) => vals.some((v) => v > 0))
      .map(([provider, vals]) => ({
        label: provider.charAt(0).toUpperCase() + provider.slice(1),
        data: vals,
        backgroundColor: PROVIDER_COLORS[provider]?.bg,
        borderColor: PROVIDER_COLORS[provider]?.border,
        borderWidth: 1,
        stack: "cost",
      })),
  };

  const lineData = {
    labels,
    datasets: [
      {
        label: "Cumulative Cost ($)",
        data: cumulativeLine,
        borderColor: "rgb(99, 102, 241)",
        backgroundColor: "rgba(99, 102, 241, 0.1)",
        fill: true,
        tension: 0.3,
        pointRadius: rangedays <= 30 ? 3 : 1,
        pointHoverRadius: 5,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      tooltip: {
        callbacks: {
          label: (ctx) => ` $${ctx.parsed.y.toFixed(4)}`,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: { callback: (v) => `$${v.toFixed(4)}` },
      },
    },
  };

  const orgChartOptions = {
    ...chartOptions,
    indexAxis: "y",
    plugins: { ...chartOptions.plugins, legend: { display: false } },
    scales: {
      x: { beginAtZero: true, ticks: { callback: (v) => `$${v.toFixed(4)}` } },
    },
  };

  return (
    <Paper sx={{ p: 3, mb: 4 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, flexWrap: "wrap", gap: 1 }}>
        <Box>
          <Typography variant="h6">LLM Cost Tracking</Typography>
          <Typography variant="body2" color="text.secondary">
            Costs sourced directly from NLP call logs ({totalCalls.toLocaleString()} calls
            {totalCost > 0 ? `, $${totalCost.toFixed(4)} total` : ""})
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
          <ToggleButtonGroup
            value={rangedays}
            exclusive
            onChange={(_, v) => v && onRangeChange(v)}
            size="small"
          >
            {RANGE_OPTIONS.map((o) => (
              <ToggleButton key={o.value} value={o.value} sx={{ px: 1.5 }}>
                {o.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <ToggleButtonGroup
            value={view}
            exclusive
            onChange={(_, v) => v && setView(v)}
            size="small"
          >
            <ToggleButton value="cumulative" sx={{ px: 1.5 }}>Cumulative</ToggleButton>
            <ToggleButton value="daily" sx={{ px: 1.5 }}>Daily</ToggleButton>
            <ToggleButton value="by-org" sx={{ px: 1.5 }}>By Org</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : calls.length === 0 ? (
        <Alert severity="info">
          No NLP call records found for the selected period.
        </Alert>
      ) : view === "by-org" ? (
        <>
          <Box sx={{ height: Math.max(160, orgData.labels.length * 40), mb: 3 }}>
            <Bar data={orgData} options={orgChartOptions} />
          </Box>
          <OrgTable calls={calls} totalCost={totalCost} />
        </>
      ) : (
        <Box sx={{ height: 300 }}>
          {view === "cumulative" ? (
            <Line data={lineData} options={chartOptions} />
          ) : (
            <Bar data={barData} options={{ ...chartOptions, scales: { ...chartOptions.scales, x: { stacked: true }, y: { ...chartOptions.scales.y, stacked: true } } }} />
          )}
        </Box>
      )}
    </Paper>
  );
};

const StatCard = ({ title, value, subtitle, icon, color = "primary" }) => (
  <Paper
    sx={{
      p: 2,
      textAlign: "center",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      justifyContent: "flex-start",
      alignItems: "center",
      bgcolor: `${color}.50`,
    }}
  >
    {React.cloneElement(icon, {
      color: color,
      sx: { fontSize: 28, mb: 0.5 },
    })}
    <Typography variant="h6">{value}</Typography>
    <Typography
      variant="subtitle2"
      color="text.secondary"
      sx={{
        display: "block",
        mb: 1,
        fontSize: "0.75rem",
        fontWeight: 500,
        textTransform: "uppercase",
        letterSpacing: "0.5px",
      }}
    >
      {title}
    </Typography>
    {subtitle && (
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ fontSize: "0.75rem" }}
      >
        {subtitle}
      </Typography>
    )}
  </Paper>
);

const SourceTable = ({ sources }) => {
  const sortedSources = Object.entries(sources).sort(
    ([, a], [, b]) => b.cost - a.cost
  );

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Source</TableCell>
            <TableCell align="right">Total Tokens</TableCell>
            <TableCell align="right">API Calls</TableCell>
            <TableCell align="right">Estimated Cost</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedSources.map(([name, data]) => (
            <TableRow key={name}>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {name}
                </Typography>
              </TableCell>
              <TableCell align="right">
                {data.tokens.toLocaleString()}
              </TableCell>
              <TableCell align="right">{data.calls.toLocaleString()}</TableCell>
              <TableCell align="right">
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {formatCost(data.cost)}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const ModelTable = ({ sources }) => {
  const modelAggregates = {};

  Object.values(sources).forEach((sourceData) => {
    Object.entries(sourceData.models).forEach(([modelName, modelData]) => {
      if (!modelAggregates[modelName]) {
        modelAggregates[modelName] = { tokens: 0, calls: 0, cost: 0 };
      }
      modelAggregates[modelName].tokens += modelData.tokens;
      modelAggregates[modelName].calls += modelData.calls;
      modelAggregates[modelName].cost += modelData.cost;
    });
  });

  const sortedModels = Object.entries(modelAggregates).sort(
    ([, a], [, b]) => b.cost - a.cost
  );

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Model</TableCell>
            <TableCell align="right">Total Tokens</TableCell>
            <TableCell align="right">API Calls</TableCell>
            <TableCell align="right">Estimated Cost</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedModels.map(([modelName, data]) => (
            <TableRow key={modelName}>
              <TableCell>
                <Chip label={modelName} size="small" />
              </TableCell>
              <TableCell align="right">
                {data.tokens.toLocaleString()}
              </TableCell>
              <TableCell align="right">{data.calls.toLocaleString()}</TableCell>
              <TableCell align="right">
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {formatCost(data.cost)}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const UserTable = ({ users }) => {
  const sortedUsers = Object.entries(users).sort(
    ([, a], [, b]) => b.cost - a.cost
  );

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>User ID</TableCell>
            <TableCell align="right">Total Tokens</TableCell>
            <TableCell align="right">API Calls</TableCell>
            <TableCell align="right">Estimated Cost</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedUsers.map(([userId, data]) => (
            <TableRow key={userId}>
              <TableCell>
                <Typography
                  variant="body2"
                  sx={{ fontFamily: "monospace", fontSize: "0.85rem" }}
                >
                  {userId}
                </Typography>
              </TableCell>
              <TableCell align="right">
                {data.tokens.toLocaleString()}
              </TableCell>
              <TableCell align="right">{data.calls.toLocaleString()}</TableCell>
              <TableCell align="right">
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {formatCost(data.cost)}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const UsageMonitorPage = () => {
  const { userData } = useContext(AuthContext);
  const navigate = useNavigate();

  const [monthlyData, setMonthlyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [llmCalls, setLlmCalls] = useState([]);
  const [llmLoading, setLlmLoading] = useState(true);
  const [rangedays, setRangedays] = useState(30);

  const availableMonths = useMemo(() => {
    const months = [];
    const now = new Date();

    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const yearMonth = `${date.getFullYear()}-${String(
        date.getMonth() + 1
      ).padStart(2, "0")}`;
      const displayName = date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
      });
      months.push({ value: yearMonth, label: displayName });
    }

    return months;
  }, []);

  const [selectedMonth, setSelectedMonth] = useState(availableMonths[0].value);

  const selectedMonthLabel = useMemo(
    () =>
      availableMonths.find((m) => m.value === selectedMonth)?.label ||
      selectedMonth,
    [availableMonths, selectedMonth]
  );

  useEffect(() => {
    const fetchUsageData = async () => {
      try {
        setLoading(true);

        const doc = await db
          .collection("usage_monthly")
          .doc(selectedMonth)
          .get();

        if (!doc.exists) {
          setMonthlyData(null);
        } else {
          const data = doc.data();
          const costData = calculateAggregatedCost(data);
          setMonthlyData(costData);
        }
      } catch (error) {
        console.error("Failed to load usage data:", error);
        setError("Failed to load usage data");
      } finally {
        setLoading(false);
      }
    };

    if (userData?.admin) {
      fetchUsageData();
    }
  }, [userData, selectedMonth]);

  useEffect(() => {
    if (!userData?.admin) return;

    const fetchLlmCalls = async () => {
      setLlmLoading(true);
      try {
        const since = new Date();
        since.setDate(since.getDate() - rangedays);

        const snap = await db
          .collection("firestore_llmcalls")
          .where("date", ">=", since)
          .orderBy("date", "asc")
          .get();

        setLlmCalls(
          snap.docs.map((d) => {
            const { date, cost, provider, organization_id } = d.data();
            return { date, cost, provider, organization_id };
          })
        );
      } catch (err) {
        console.error("Failed to load LLM call logs:", err);
      } finally {
        setLlmLoading(false);
      }
    };

    fetchLlmCalls();
  }, [userData, rangedays]);

  if (userData && !userData?.admin) navigate("/dashboard");

  if (userData?.admin)
    return (
      <Box sx={{ mb: 4 }}>
        <Box sx={{ mb: 4 }}>
          <DashboardPageHeader
            title="Usage Monitor"
            subtitle={
              <Typography sx={{ display: "block", maxWidth: "40rem" }}>
                Track OpenAI API usage and estimated costs across different
                months.
              </Typography>
            }
          />

          <Alert severity="info" sx={{ mb: 3 }}>
            Monthly breakdown costs are estimates based on October 2025 OpenAI
            pricing. The LLM Cost Tracking chart below uses actual costs
            recorded at call time from NLP call logs.
          </Alert>

          <LLMCostChart
            calls={llmCalls}
            loading={llmLoading}
            rangedays={rangedays}
            onRangeChange={setRangedays}
          />

          <Box sx={{ mb: 3 }}>
            <FormControl sx={{ minWidth: 250 }}>
              <Select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                size="small"
              >
                {availableMonths.map((month) => (
                  <MenuItem key={month.value} value={month.value}>
                    {month.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
        </Box>

        {loading ? (
          <CircularProgress size={30} />
        ) : !monthlyData ? (
          <Alert severity="warning">
            No usage data found for {selectedMonthLabel}. Data will appear once
            tracking is enabled and API calls are made.
          </Alert>
        ) : (
          <>
            <Grid container spacing={2} sx={{ mb: 3, alignItems: "stretch" }}>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Total Cost"
                  value={formatCost(monthlyData.totalCost, true)}
                  subtitle="OpenAI API spending this month"
                  icon={<MonetizationOnRounded />}
                  color="success"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Total Tokens"
                  value={monthlyData.totalTokens.toLocaleString()}
                  subtitle="Input + output combined"
                  icon={<TrendingUpRounded />}
                  color="primary"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Avg Cost/User"
                  value={formatCost(monthlyData.avgCostPerUser)}
                  subtitle="Average spending per user"
                  icon={<SmartToyRounded />}
                  color="info"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Total Users"
                  value={monthlyData.userCount.toLocaleString()}
                  subtitle="Unique users this month"
                  icon={<CallMadeRounded />}
                  color="warning"
                />
              </Grid>
            </Grid>

            <Typography variant="h6" sx={{ mb: 2, mt: 4 }}>
              Cost by Source
            </Typography>
            <SourceTable sources={monthlyData.sources} />

            <Typography variant="h6" sx={{ mb: 2, mt: 4 }}>
              Cost by Model
            </Typography>
            <ModelTable sources={monthlyData.sources} />

            {monthlyData.users &&
              Object.keys(monthlyData.users).length > 0 && (
                <>
                  <Typography variant="h6" sx={{ mb: 2, mt: 4 }}>
                    Cost by User
                  </Typography>
                  <UserTable users={monthlyData.users} />
                </>
              )}
          </>
        )}
      </Box>
    );

  return <CircularProgress />;
};

export default UsageMonitorPage;
